import { type RuntimeConfig, runtimeConfigSchema } from './schema'

export class ConfigError extends Error {
  constructor(
    message: string,
    public readonly issues: ReadonlyArray<{
      path: string
      message: string
    }> = []
  ) {
    super(message)
    this.name = 'ConfigError'
  }
}

let current: RuntimeConfig | null = null

/**
 * Candidate config URLs, most specific first:
 *  1. `/config/<hostname>.json` — the deployed layout: one static site serves several
 *     hostnames (spark.* and app.*), each with its own file. No server-side rewrite needed.
 *  2. `/config.json` — local development (public/config.json or the SPARK_CONFIG plugin).
 */
export function configUrlCandidates(hostname = window.location.hostname) {
  return [`/config/${hostname.toLowerCase()}.json`, '/config.json']
}

/** Fetches and validates the first available config. Called once by `main.tsx` before anything else loads. */
export async function loadRuntimeConfig(
  urls: readonly string[] = configUrlCandidates()
): Promise<RuntimeConfig> {
  const failures: string[] = []
  for (const url of urls) {
    let response: Response
    try {
      response = await fetch(url, { cache: 'no-store' })
    } catch (cause) {
      failures.push(`${url}: ${String(cause)}`)
      continue
    }
    if (response.status === 404) {
      failures.push(`${url}: HTTP 404`)
      continue
    }
    if (!response.ok) {
      throw new ConfigError(`Could not fetch ${url}: HTTP ${response.status}.`)
    }
    let json: unknown
    try {
      json = await response.json()
    } catch {
      throw new ConfigError(`${url} is not valid JSON.`)
    }
    return setRuntimeConfig(json)
  }
  throw new ConfigError(
    `No runtime config found for host "${window.location.hostname}" (${failures.join('; ')}). ` +
      'Deployed: add deploy/config/<hostname>.json. Local: npm run config:use -- <name> or set SPARK_CONFIG.'
  )
}

/** Validates and installs a config object. Exposed for tests and for `main.tsx`. */
export function setRuntimeConfig(input: unknown): RuntimeConfig {
  const parsed = runtimeConfigSchema.safeParse(input)
  if (!parsed.success) {
    throw new ConfigError(
      'config.json is invalid.',
      parsed.error.issues.map((i) => ({
        path: i.path.join('.'),
        message: i.message,
      }))
    )
  }
  current = parsed.data
  return current
}

/** Synchronous access for non-React code (API client, MSAL, logger). Throws before bootstrap. */
export function getRuntimeConfig(): RuntimeConfig {
  if (!current) {
    throw new Error(
      'Runtime config accessed before loadRuntimeConfig() completed.'
    )
  }
  return current
}

export function resetRuntimeConfigForTests() {
  current = null
}
